import React, { useState, useEffect } from 'react';
import { Clock, Sun, Moon, Save, AlertCircle, RefreshCw, Info, Calendar, Pencil, Check, Building2, Globe } from 'lucide-react';
import { calendarSettingsService, CreateWorkingHoursDto, RamadanDates } from '../../services/calendarSettingsService';
import { fetchCampuses, Campus } from '../../../../services/etablissementService';

interface WorkingHoursFormProps {
  canManage?: boolean;
}

export const WorkingHoursForm: React.FC<WorkingHoursFormProps> = ({ canManage = true }) => {
  const [configId, setConfigId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [upcomingRamadan, setUpcomingRamadan] = useState<RamadanDates | null>(null);
  const [loadingRamadan, setLoadingRamadan] = useState(false);

  // Campus management
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [hasMultipleCampuses, setHasMultipleCampuses] = useState(false);
  const [configMode, setConfigMode] = useState<'common' | 'per-campus'>('common');
  const [selectedCampusId, setSelectedCampusId] = useState<string | null>(null);

  // Mode édition global pour toute la page
  const [isEditing, setIsEditing] = useState(false);
  // Mode édition spécifique pour la section période Ramadan
  const [isEditingRamadanPeriod, setIsEditingRamadanPeriod] = useState(false);

  const [formData, setFormData] = useState<CreateWorkingHoursDto>({
    regularStartTime: '08:00',
    regularEndTime: '18:00',
    ramadanStartTime: '09:00',
    ramadanEndTime: '16:00',
    ramadanAutoUpdate: true,
    breakStartTime: '12:00',
    breakDuration: 60,
  });

  // Store original data for cancel
  const [originalData, setOriginalData] = useState<CreateWorkingHoursDto | null>(null);

  useEffect(() => {
    loadCampuses();
    loadUpcomingRamadan();
  }, []);

  useEffect(() => {
    loadConfig();
  }, [selectedCampusId, configMode]);

  const loadCampuses = async () => {
    try {
      const token = localStorage.getItem('nexedu_access_token');
      if (!token) return;

      const campusList = await fetchCampuses(token);
      setCampuses(campusList);
      setHasMultipleCampuses(campusList.length > 1);
    } catch (err) {
      console.error('Failed to load campuses:', err);
    }
  };

  const loadConfig = async () => {
    try {
      setLoading(true);
      const campusId = configMode === 'per-campus' ? selectedCampusId || undefined : undefined;
      const data = await calendarSettingsService.getWorkingHours(campusId);
      if (data) {
        setConfigId(data.id);
        const loadedData = {
          campusId: data.campusId,
          regularStartTime: data.regularStartTime,
          regularEndTime: data.regularEndTime,
          ramadanStartTime: data.ramadanStartTime || '',
          ramadanEndTime: data.ramadanEndTime || '',
          ramadanStartDate: data.ramadanStartDate?.split('T')[0] || '',
          ramadanEndDate: data.ramadanEndDate?.split('T')[0] || '',
          ramadanAutoUpdate: data.ramadanAutoUpdate ?? true,
          breakStartTime: data.breakStartTime || '',
          breakDuration: data.breakDuration || 60,
        };
        setFormData(loadedData);
        setOriginalData(loadedData);
      } else {
        // Reset to defaults if no config found
        setConfigId(null);
        const defaultData = {
          campusId: configMode === 'per-campus' ? selectedCampusId || undefined : undefined,
          regularStartTime: '08:00',
          regularEndTime: '18:00',
          ramadanStartTime: '09:00',
          ramadanEndTime: '16:00',
          ramadanAutoUpdate: true,
          breakStartTime: '12:00',
          breakDuration: 60,
        };
        setFormData(defaultData);
        setOriginalData(defaultData);
      }
    } catch (err) {
      console.error('Failed to load working hours:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUpcomingRamadan = async () => {
    try {
      setLoadingRamadan(true);
      const data = await calendarSettingsService.getUpcomingRamadanDates();
      console.log('Loaded upcoming Ramadan:', data);
      setUpcomingRamadan(data);
    } catch (err) {
      console.error('Failed to load upcoming Ramadan dates:', err);
    } finally {
      setLoadingRamadan(false);
    }
  };

  // Actualise les dates depuis le serveur puis les applique au formulaire
  const handleRefreshAndApplyDates = async () => {
    try {
      setLoadingRamadan(true);
      setError(null);
      console.log('Fetching Ramadan dates...');
      const data = await calendarSettingsService.getUpcomingRamadanDates();
      console.log('Received Ramadan dates:', data);
      if (data) {
        setUpcomingRamadan(data);
        setFormData(prev => ({
          ...prev,
          ramadanStartDate: data.startDate,
          ramadanEndDate: data.endDate,
          ramadanAutoUpdate: true,
        }));
        console.log('Applied dates to form:', data.startDate, data.endDate);
      } else {
        setError('Impossible de récupérer les dates du Ramadan');
      }
    } catch (err) {
      console.error('Error fetching Ramadan dates:', err);
      setError('Erreur lors du chargement des dates du Ramadan');
    } finally {
      setLoadingRamadan(false);
    }
  };

  // Applique les dates déjà chargées (sans refetch)
  const handleApplyCalculatedDates = () => {
    if (upcomingRamadan) {
      setFormData(prev => ({
        ...prev,
        ramadanStartDate: upcomingRamadan.startDate,
        ramadanEndDate: upcomingRamadan.endDate,
        ramadanAutoUpdate: true,
      }));
    }
  };

  const handleStartEditing = () => {
    setOriginalData({ ...formData });
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    if (originalData) {
      setFormData(originalData);
    }
    setIsEditing(false);
    setIsEditingRamadanPeriod(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    try {
      setSaving(true);

      const dataToSave = {
        ...formData,
        regularStartTime: formData.regularStartTime || '08:00',
        regularEndTime: formData.regularEndTime || '18:00',
        campusId: configMode === 'per-campus' ? selectedCampusId || undefined : undefined,
      };

      console.log('Saving config:', { configId, dataToSave });

      const result = await calendarSettingsService.createOrUpdateWorkingHours(dataToSave, configId || undefined);
      setConfigId(result.id);
      setOriginalData({ ...formData });

      // Après sauvegarde, remettre en mode lecture
      setIsEditing(false);
      setIsEditingRamadanPeriod(false);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      console.error('Failed to save:', err);
      const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Non définie';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Header with Edit/Save Button - only shown if user can manage */}
      {canManage && (
        <div className="flex justify-end">
          {!isEditing ? (
            <button
              type="button"
              onClick={handleStartEditing}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Pencil size={20} />
              Modifier
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCancelEditing}
                className="flex items-center gap-2 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <Save size={20} />
                )}
                Enregistrer
              </button>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <Check size={20} />
          <span>Configuration enregistrée avec succès</span>
        </div>
      )}

      {/* Campus Selection (only show if multiple campuses) */}
      {hasMultipleCampuses && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Building2 className="text-purple-600" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Configuration par campus</h3>
              <p className="text-sm text-gray-500">
                Choisissez si les horaires sont communs à tous les campus ou spécifiques à chaque campus
              </p>
            </div>
          </div>

          <div className="flex gap-4 mb-4">
            <button
              type="button"
              onClick={() => {
                setConfigMode('common');
                setSelectedCampusId(null);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                configMode === 'common'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
              }`}
            >
              <Globe size={18} />
              Communs à tous
            </button>
            <button
              type="button"
              onClick={() => {
                setConfigMode('per-campus');
                if (campuses.length > 0 && !selectedCampusId) {
                  setSelectedCampusId(campuses[0].id);
                }
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                configMode === 'per-campus'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
              }`}
            >
              <Building2 size={18} />
              Par campus
            </button>
          </div>

          {configMode === 'per-campus' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner le campus
              </label>
              <select
                value={selectedCampusId || ''}
                onChange={(e) => setSelectedCampusId(e.target.value)}
                className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              >
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Regular Hours */}
      <div className="bg-gray-50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Clock className="text-blue-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Horaires réguliers</h3>
            <p className="text-sm text-gray-500">Horaires de travail standards</p>
          </div>
        </div>

        {!isEditing ? (
          // View Mode
          <div className="grid grid-cols-2 gap-6">
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <p className="text-sm text-gray-500">Heure de début</p>
              <p className="font-medium text-gray-900">{formData.regularStartTime || '08:00'}</p>
            </div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <p className="text-sm text-gray-500">Heure de fin</p>
              <p className="font-medium text-gray-900">{formData.regularEndTime || '18:00'}</p>
            </div>
          </div>
        ) : (
          // Edit Mode
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heure de début
              </label>
              <input
                type="time"
                value={formData.regularStartTime}
                onChange={(e) => setFormData({ ...formData, regularStartTime: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heure de fin
              </label>
              <input
                type="time"
                value={formData.regularEndTime}
                onChange={(e) => setFormData({ ...formData, regularEndTime: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
          </div>
        )}
      </div>

      {/* Break Time */}
      <div className="bg-gray-50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <Sun className="text-green-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Pause déjeuner</h3>
            <p className="text-sm text-gray-500">Configuration de la pause</p>
          </div>
        </div>

        {!isEditing ? (
          // View Mode
          <div className="grid grid-cols-2 gap-6">
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <p className="text-sm text-gray-500">Heure de début de pause</p>
              <p className="font-medium text-gray-900">{formData.breakStartTime || 'Non définie'}</p>
            </div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <p className="text-sm text-gray-500">Durée de la pause</p>
              <p className="font-medium text-gray-900">{formData.breakDuration ? `${formData.breakDuration} minutes` : 'Non définie'}</p>
            </div>
          </div>
        ) : (
          // Edit Mode
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heure de début de pause
              </label>
              <input
                type="time"
                value={formData.breakStartTime || ''}
                onChange={(e) => setFormData({ ...formData, breakStartTime: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Durée de la pause (minutes)
              </label>
              <input
                type="number"
                value={formData.breakDuration || ''}
                onChange={(e) => setFormData({ ...formData, breakDuration: parseInt(e.target.value) || undefined })}
                min="0"
                max="180"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Ramadan Hours */}
      <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Moon className="text-amber-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Horaires Ramadan</h3>
            <p className="text-sm text-gray-500">Horaires adaptés pendant le mois de Ramadan</p>
          </div>
        </div>

        {/* Ramadan Working Hours */}
        {!isEditing ? (
          // View Mode
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="p-4 bg-white rounded-lg border border-amber-200">
              <p className="text-sm text-gray-500">Heure de début</p>
              <p className="font-medium text-gray-900">{formData.ramadanStartTime || 'Non définie'}</p>
            </div>
            <div className="p-4 bg-white rounded-lg border border-amber-200">
              <p className="text-sm text-gray-500">Heure de fin</p>
              <p className="font-medium text-gray-900">{formData.ramadanEndTime || 'Non définie'}</p>
            </div>
          </div>
        ) : (
          // Edit Mode
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heure de début
              </label>
              <input
                type="time"
                value={formData.ramadanStartTime || ''}
                onChange={(e) => setFormData({ ...formData, ramadanStartTime: e.target.value })}
                className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heure de fin
              </label>
              <input
                type="time"
                value={formData.ramadanEndTime || ''}
                onChange={(e) => setFormData({ ...formData, ramadanEndTime: e.target.value })}
                className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
              />
            </div>
          </div>
        )}

        {/* Ramadan Period Section */}
        <div className="border-t border-amber-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Calendar className="text-amber-600" size={20} />
              <div>
                <p className="font-medium text-gray-900">Période du Ramadan</p>
                <p className="text-sm text-gray-500">
                  {formData.ramadanAutoUpdate
                    ? 'Mise à jour automatique activée'
                    : 'Mode manuel - dates personnalisées'}
                </p>
              </div>
            </div>
            {isEditing && !isEditingRamadanPeriod && (
              <button
                type="button"
                onClick={() => setIsEditingRamadanPeriod(true)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
              >
                <Pencil size={16} />
                Modifier
              </button>
            )}
          </div>

          {/* View Mode for Ramadan Period */}
          {!isEditingRamadanPeriod && (
            <div className="p-4 bg-white rounded-lg border border-amber-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Date de début</p>
                  <p className="font-medium text-gray-900">{formatDate(formData.ramadanStartDate || '')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date de fin</p>
                  <p className="font-medium text-gray-900">{formatDate(formData.ramadanEndDate || '')}</p>
                </div>
              </div>
              {formData.ramadanAutoUpdate && (
                <p className="mt-3 text-xs text-amber-600 flex items-center gap-1">
                  <Info size={14} />
                  Les dates seront automatiquement mises à jour chaque année après le Ramadan
                </p>
              )}
            </div>
          )}

          {/* Edit Mode for Ramadan Period */}
          {isEditingRamadanPeriod && (
            <div className="space-y-4">
              {/* Actualiser Button - Load calculated dates */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleRefreshAndApplyDates}
                  disabled={loadingRamadan}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 disabled:opacity-50 transition-colors"
                  title="Charger les dates calculées du Ramadan"
                >
                  <RefreshCw size={16} className={loadingRamadan ? 'animate-spin' : ''} />
                  Actualiser les dates
                </button>
              </div>

              {/* Upcoming Ramadan Info */}
              {upcomingRamadan && (
                <div className="p-4 bg-white rounded-lg border border-amber-200">
                  <div className="flex items-start gap-3">
                    <Info className="text-amber-600 mt-0.5" size={18} />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        Ramadan {upcomingRamadan.gregorianYear} ({upcomingRamadan.hijriYear} H)
                      </p>
                      <p className="text-sm text-gray-600">
                        Dates calculées : {formatDate(upcomingRamadan.startDate)} - {formatDate(upcomingRamadan.endDate)}
                      </p>
                      {upcomingRamadan.isApproximate && (
                        <p className="text-xs text-amber-600 mt-1">
                          * Dates approximatives, peuvent varier selon l'observation de la lune au Maroc
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={handleApplyCalculatedDates}
                        className="mt-2 flex items-center gap-1 text-sm text-amber-700 hover:text-amber-800 font-medium"
                      >
                        <Check size={14} />
                        Appliquer ces dates
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Auto-update toggle */}
              <div className="p-4 bg-white rounded-lg border border-amber-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Mise à jour automatique</p>
                    <p className="text-sm text-gray-500">
                      Les dates seront automatiquement calculées chaque année
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.ramadanAutoUpdate ?? true}
                      onChange={(e) => setFormData({ ...formData, ramadanAutoUpdate: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                  </label>
                </div>
              </div>

              {/* Date inputs */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de début du Ramadan
                  </label>
                  <input
                    type="date"
                    value={formData.ramadanStartDate || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      ramadanStartDate: e.target.value,
                      ramadanAutoUpdate: false
                    })}
                    className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de fin du Ramadan
                  </label>
                  <input
                    type="date"
                    value={formData.ramadanEndDate || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      ramadanEndDate: e.target.value,
                      ramadanAutoUpdate: false
                    })}
                    className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </form>
  );
};
